import React, { useState, useEffect, useRef } from 'react';
import { CalendarIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { CustomDatePicker } from './CustomDatePicker';
import { convertLocalToUTC, formatDateTime } from '../../utils/dateFormat';
import { TIME_PRESETS, activePreset } from './timePresets';

interface EnhancedDateTimePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  minDate?: string;
  maxDate?: string;
}

export const EnhancedDateTimePicker: React.FC<EnhancedDateTimePickerProps> = ({
  value = '',
  onChange,
  onValidationChange,
  label = 'Date & Time',
  placeholder,
  required = false,
  disabled = false,
  error,
  className = '',
  minDate,
  maxDate,
}) => {
  const [dateValue, setDateValue] = useState('');
  const [hour, setHour] = useState('');
  const [minute, setMinute] = useState('');
  const [ampm, setAmpm] = useState<'AM' | 'PM'>('AM');
  const [isValid, setIsValid] = useState(true);
  const [isTouched, setIsTouched] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const minuteInputRef = useRef<HTMLInputElement>(null);
  // The last value this picker sent up. When it comes back as `value` the fields already show it,
  // and parsing it again would rewrite what is being typed: a minute of '1' became '01' (AMP-193).
  const lastEmitted = useRef<string | undefined>(undefined);

  const emit = (next: string) => {
    lastEmitted.current = next;
    onChange?.(next);
  };

  // Parse UTC datetime to local date and time
  useEffect(() => {
    const fromHere = value === lastEmitted.current;
    lastEmitted.current = undefined;
    if (fromHere) return;
    if (value) {
      try {
        const utcDate = new Date(value);
        if (!isNaN(utcDate.getTime())) {
          // Extract date in YYYY-MM-DD format for the date picker
          const year = utcDate.getFullYear();
          const month = utcDate.getMonth();
          const day = utcDate.getDate();
          
          // Convert date to UTC format for CustomDatePicker
          const utcDateOnly = new Date(Date.UTC(year, month, day));
          setDateValue(utcDateOnly.toISOString());
          
          // Extract time components
          let hours = utcDate.getHours();
          const minutes = utcDate.getMinutes();
          
          // Convert to 12-hour format
          const period = hours >= 12 ? 'PM' : 'AM';
          if (hours === 0) hours = 12;
          else if (hours > 12) hours -= 12;
          
          setHour(String(hours));
          setMinute(String(minutes).padStart(2, '0'));
          setAmpm(period);
        }
      } catch {
        setDateValue('');
        setHour('');
        setMinute('');
        setAmpm('AM');
      }
    } else {
      setDateValue('');
      setHour('');
      setMinute('');
      setAmpm('AM');
    }
  }, [value]);

  // Combine date and time and convert to UTC
  const handleDateTimeChange = (newDate?: string, newHour?: string, newMinute?: string, newAmpm?: 'AM' | 'PM') => {
    const currentDate = newDate !== undefined ? newDate : dateValue;
    const currentHour = newHour !== undefined ? newHour : hour;
    const currentMinute = newMinute !== undefined ? newMinute : minute;
    const currentAmpm = newAmpm !== undefined ? newAmpm : ampm;
    const hourNum = parseInt(currentHour);

    // An hour of '0' is still being typed (01-09), so there is no time to send yet
    if (currentDate && hourNum >= 1 && hourNum <= 12 && currentMinute) {
      try {
        // Parse the UTC date string to get the date components
        const dateObj = new Date(currentDate);
        const year = dateObj.getUTCFullYear();
        const month = dateObj.getUTCMonth();
        const day = dateObj.getUTCDate();

        // Convert hour to 24-hour format
        let hour24 = hourNum;
        if (currentAmpm === 'PM' && hour24 !== 12) hour24 += 12;
        if (currentAmpm === 'AM' && hour24 === 12) hour24 = 0;
        
        // Create local datetime
        const localDateTime = new Date(year, month, day, hour24, parseInt(currentMinute));
        
        // Convert to UTC
        const utcString = localDateTime.toISOString();
        emit(utcString);
      } catch {
        emit('');
      }
    } else if (!currentDate && !currentHour && !currentMinute) {
      emit('');
    }
    
    setIsTouched(true);
  };

  const handleDateChange = (utcDate: string) => {
    setDateValue(utcDate);
    handleDateTimeChange(utcDate, undefined, undefined, undefined);
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHour = e.target.value.replace(/\D/g, ''); // Only digits

    // Ignore a digit that would leave 1-12 instead of rewriting the field; a lone '0' starts 01-09 (AMP-193)
    const hourNum = parseInt(newHour);
    const partial = newHour === '' || newHour === '0';
    if (newHour.length > 2 || (!partial && (hourNum < 1 || hourNum > 12))) return;

    setHour(newHour);
    handleDateTimeChange(undefined, newHour, undefined, undefined);

    // Move on to minutes once no further hour digit fits: two digits, or a first digit of 2-9
    if (newHour.length > hour.length && (newHour.length === 2 || /^[2-9]$/.test(newHour))) {
      minuteInputRef.current?.focus();
      minuteInputRef.current?.select(); // typing replaces a minute already there
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newMinute = e.target.value;
    
    // Remove non-digits
    newMinute = newMinute.replace(/\D/g, '');
    
    // Limit to 2 digits max
    if (newMinute.length > 2) {
      newMinute = newMinute.slice(0, 2);
    }
    
    // Ignore a digit that would go past 59 instead of rewriting the field (AMP-193)
    if (newMinute && parseInt(newMinute) > 59) return;
    
    setMinute(newMinute);
    handleDateTimeChange(undefined, undefined, newMinute, undefined);
  };

  // Reads the field, not `hour`: the jump to minutes blurs it before the typed digit reaches state
  const handleHourBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const typed = e.target.value;
    if (typed && parseInt(typed) === 0) {
      setHour('12');
      handleDateTimeChange(undefined, '12', undefined, undefined);
    } else if (typed.length === 2 && typed.startsWith('0')) {
      setHour(String(parseInt(typed))); // '04' reads '4', like a stored time
    }
  };

  const handleMinuteBlur = () => {
    if (minute && minute.length === 1) {
      const paddedMinute = minute.padStart(2, '0');
      setMinute(paddedMinute);
    }
  };

  const handleAmpmChange = (newAmpm: 'AM' | 'PM') => {
    setAmpm(newAmpm);
    handleDateTimeChange(undefined, undefined, undefined, newAmpm);
  };

  // Quick time buttons
  const setQuickTime = (hours: number, minutes: number = 0) => {
    const period = hours >= 12 ? 'PM' : 'AM';
    let displayHour = hours;
    if (hours === 0) displayHour = 12;
    else if (hours > 12) displayHour = hours - 12;
    
    setHour(String(displayHour));
    setMinute(String(minutes).padStart(2, '0'));
    setAmpm(period);
    handleDateTimeChange(undefined, String(displayHour), String(minutes).padStart(2, '0'), period);
  };

  const setCurrentTime = () => {
    const now = new Date();
    setQuickTime(now.getHours(), now.getMinutes());
  };

  // Validate datetime
  const validateDateTime = () => {
    if (!dateValue && !hour && !minute) {
      const valid = !required;
      setIsValid(valid);
      setValidationMessage(required ? 'Date and time are required' : '');
      onValidationChange?.(valid);
      return;
    }

    if (dateValue && (!hour || !minute)) {
      setIsValid(false);
      setValidationMessage('Please enter both hour and minute');
      onValidationChange?.(false);
      return;
    }

    if (!dateValue && (hour || minute)) {
      setIsValid(false);
      setValidationMessage('Please select a date');
      onValidationChange?.(false);
      return;
    }

    // Check if time is valid
    const hourNum = parseInt(hour);
    const minuteNum = parseInt(minute);
    
    if (hour && (hourNum < 1 || hourNum > 12)) {
      setIsValid(false);
      setValidationMessage('Hour must be between 1-12');
      onValidationChange?.(false);
      return;
    }

    if (minute && (minuteNum < 0 || minuteNum > 59)) {
      setIsValid(false);
      setValidationMessage('Minutes must be between 00-59');
      onValidationChange?.(false);
      return;
    }

    // Check minimum datetime against minDate itself (it used to be checked against "now", which
    // rejected any earlier time today whatever minDate was).
    if (minDate && value) {
      if (new Date(value) < new Date(minDate)) {
        setIsValid(false);
        setValidationMessage('Please select a time on or after the earliest allowed time');
        onValidationChange?.(false);
        return;
      }
    }

    setIsValid(true);
    setValidationMessage('');
    onValidationChange?.(true);
  };

  useEffect(() => {
    if (isTouched || value) {
      validateDateTime();
    }
  }, [dateValue, hour, minute, ampm, value, isTouched, required, minDate, maxDate]);

  // Handle keyboard navigation
  const handleHourKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newHour = hour ? Math.min(12, parseInt(hour) + 1) : 1;
      setHour(String(newHour));
      handleDateTimeChange(undefined, String(newHour), undefined, undefined);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newHour = hour ? Math.max(1, parseInt(hour) - 1) : 12;
      setHour(String(newHour));
      handleDateTimeChange(undefined, String(newHour), undefined, undefined);
    }
  };

  const handleMinuteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const current = minute ? parseInt(minute) : 0;
      const newMinute = Math.min(59, current + 1);
      const newMinuteStr = String(newMinute).padStart(2, '0');
      setMinute(newMinuteStr);
      handleDateTimeChange(undefined, undefined, newMinuteStr, undefined);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const current = minute ? parseInt(minute) : 0;
      const newMinute = Math.max(0, current - 1);
      const newMinuteStr = String(newMinute).padStart(2, '0');
      setMinute(newMinuteStr);
      handleDateTimeChange(undefined, undefined, newMinuteStr, undefined);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Date Picker */}
        <div>
          <CustomDatePicker
            value={dateValue}
            onChange={handleDateChange}
            placeholder="Select date"
            minDate={minDate}
            maxDate={maxDate}
            disabled={disabled}
          />
        </div>

        {/* Time Controls */}
        <div className="space-y-3">
          {/* Time Input */}
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <ClockIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={hour}
                onChange={handleHourChange}
                onKeyDown={handleHourKeyDown}
                onBlur={handleHourBlur}
                placeholder="12"
                disabled={disabled || !dateValue}
                className={`
                  w-full pl-8 pr-2 py-2 text-center border rounded-md
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${!dateValue ? 'bg-gray-50' : ''}
                  ${error || (isTouched && !isValid) ? 'border-red-300' : 'border-gray-300'}
                `}
                maxLength={2}
              />
            </div>
            
            <span className="text-gray-500 font-medium">:</span>
            
            <div className="flex-1">
              <input
                ref={minuteInputRef}
                type="text"
                value={minute}
                onChange={handleMinuteChange}
                onKeyDown={handleMinuteKeyDown}
                onBlur={handleMinuteBlur}
                placeholder="00"
                disabled={disabled || !dateValue}
                className={`
                  w-full px-2 py-2 text-center border rounded-md
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${!dateValue ? 'bg-gray-50' : ''}
                  ${error || (isTouched && !isValid) ? 'border-red-300' : 'border-gray-300'}
                `}
                maxLength={2}
              />
            </div>
            
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <button
                type="button"
                onClick={() => handleAmpmChange('AM')}
                disabled={disabled || !dateValue}
                className={`
                  px-3 py-2 text-sm font-medium transition-colors
                  ${ampm === 'AM' 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                AM
              </button>
              <button
                type="button"
                onClick={() => handleAmpmChange('PM')}
                disabled={disabled || !dateValue}
                className={`
                  px-3 py-2 text-sm font-medium transition-colors border-l border-gray-300
                  ${ampm === 'PM' 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                PM
              </button>
            </div>
          </div>

          {/* Quick Time Buttons */}
          {(() => {
            const now = new Date();
            const active = activePreset(hour, minute, ampm, now.getHours() * 60 + now.getMinutes());
            const presetClass = (on: boolean) =>
              `px-2 py-1 text-xs rounded disabled:opacity-50 ${
                on
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`;
            return (
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={setCurrentTime}
                  disabled={disabled || !dateValue}
                  aria-pressed={active === 'now'}
                  className={presetClass(active === 'now')}
                >
                  Now
                </button>
                {TIME_PRESETS.map((preset, i) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => setQuickTime(preset.hour24, preset.minute)}
                    disabled={disabled || !dateValue}
                    aria-pressed={active === i}
                    className={presetClass(active === i)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            );
          })()}

          <p className="text-xs text-gray-500">
            Use arrow keys or type to set time
          </p>
        </div>
      </div>

      {/* Combined Display */}
      {dateValue && hour && minute && isValid && (
        <div className="text-sm text-gray-600 bg-gray-50 rounded-md p-3 border-l-4 border-primary-500">
          <div className="flex items-center">
            <CalendarIcon className="w-4 h-4 mr-2 text-primary-600" />
            <span className="font-medium">Appointment scheduled for:</span>
          </div>
          <div className="ml-6 mt-1 text-base font-medium text-gray-900">
            {formatDateTime(value)}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 flex items-center">
          <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
      
      {/* Validation Message */}
      {!error && isTouched && validationMessage && (
        <p className="text-sm text-red-600 flex items-center">
          <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          {validationMessage}
        </p>
      )}

      {/* Help Text */}
      {!dateValue && !hour && !minute && !isTouched && (
        <p className="text-xs text-gray-500">
          Select date first, then set time using the controls above
        </p>
      )}
    </div>
  );
};